import React, { useState, useEffect } from 'react';
import { getParameterByName } from '../../globalFunction';
import { useSelector } from 'react-redux';
import './index.scss';
import Icon, { ICON_TYPES, ICONS } from '@/src/components/common/Elements/Icon/Icon';
import UserActivated from '../userActivated';
import Toast from '../common/Toastv2/Toast';
import LoadingSpinner from '@/src/components/common/Elements/Spinner/SectionLoader/LoadingSpinner/LoadingSpinner';

interface NewPasswordProps {}

const NewPassword: React.FC<NewPasswordProps> = () => {
  const socket = useSelector((store: any) => store.socket.container);
  const [state, setState] = useState({
    email: '',
    disabled: false,
    type: 'text',
    showPassword: false,
    showConfirmPassword: false,
    newPassword: '',
    confirmPassword: '',
    hash: '',
    projectId: '',
    user: '',
    userId: '',
    tokenInvalid: false,
    submitting: false,
    passwordCreated: false,
  });
  const [showLinkInvalid, setShowLinkInvalid] = useState(false);
  const [toast, setToast] = useState<{ message: string; key: number } | null>(null);
  const [loading, setLoading] = useState(true);
  let toastKey = 0;

  useEffect(() => {
    const url = window.location;
    const hash = getParameterByName('hash', url);
    const projectId = getParameterByName('projectId', url);
    const user = getParameterByName('user', url);
    setState((prev) => ({ ...prev, hash, projectId, user }));
  }, []);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');
    if (token) {
      fetch(`api/user-activation/validate-token?token=${token}`)
        .then((res) => res.json())
        .then((data) => {
          setLoading(false);
          if (data.status && data.username && data['user-id']) {
            setState((prev) => ({
              ...prev,
              user: data.username,
              userId: data['user-id'],
              tokenInvalid: false,
            }));
          } else if (data && data.code === 'INVALID_OR_EXPIRED_TOKEN') {
            setState((prev) => ({ ...prev, tokenInvalid: true }));
          } else {
            setState((prev) => ({ ...prev, tokenInvalid: true }));
          }
        })
        .catch(() => {
          setLoading(false);
          setState((prev) => ({ ...prev, tokenInvalid: true }));
        });
    } else {
      setLoading(false);
    }
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setState((prev) => ({ ...prev, [name]: value }));
  };

  const handleShowPassword = (name: 'showPassword' | 'showConfirmPassword') => {
    setState((prev) => ({ ...prev, [name]: !prev[name] }));
  };

  const showToast = (type: string, message: string, duration?: number) => {
    toastKey++;
    setToast({ message, key: toastKey });
    setTimeout(() => setToast(null), duration || 10000000000);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const { newPassword, confirmPassword, userId, submitting, tokenInvalid } = state;
    if (submitting || tokenInvalid || state.passwordCreated) return;
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');
    showToast('info', 'Updating password...', 360000);

    if (newPassword !== confirmPassword) {
      showToast('error', 'Password and confirm password must be the same.');
      setState((prev) => ({ ...prev, newPassword: '', confirmPassword: '', disabled: false }));
    } else if (newPassword.length < 6) {
      showToast('error', 'Passwords at least 6 characters.');
      setState((prev) => ({ ...prev, newPassword: '', confirmPassword: '', disabled: false }));
    } else {
      setState((prev) => ({ ...prev, submitting: true }));
      fetch('api/user-activation/create-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          'user-id': userId,
          token,
          password: newPassword,
        }),
      })
        .then(async (res) => {
          setState((prev) => ({ ...prev, submitting: false }));
          if (res.ok) {
            showToast('success', 'Password successfully saved!');
            setState((prev) => ({ ...prev, passwordCreated: true }));
            setTimeout(() => {
              setShowLinkInvalid(true);
              window.location.href = '/account'; // Use native redirect
            }, 2000);
            return;
          } else {
            console.error('Password creation failed, response not OK'); // Debug log
          }
          let data;
          try {
            data = await res.json();
          } catch (e) {
            data = {};
          }
          if (data && data.code === 'INVALID_OR_EXPIRED_TOKEN') {
            setState((prev) => ({ ...prev, tokenInvalid: true }));
            showToast('error', 'Token is invalid or expired');
          } else {
            showToast('error', 'Something went wrong. Please try again.');
          }
        })
        .catch(() => {
          setState((prev) => ({ ...prev, submitting: false })); // Reset submitting
          showToast('error', 'Something went wrong. Please try again.');
        });
    }
  };

  const { showPassword, showConfirmPassword, newPassword, confirmPassword, user, tokenInvalid } =
    state;
  const isValid = newPassword && confirmPassword && newPassword === confirmPassword;

  if (loading) {
    return <LoadingSpinner />;
  }
  if (state.tokenInvalid) {
    return <UserActivated />;
  }

  return (
    <div
      className="login"
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {toast && <Toast message={toast.message} onClose={() => setToast(null)} />}
      <div className="form-signin" style={{ margin: 'auto' }}>
        <div className="border-top"></div>
        <div className="logo logo-header" style={{ padding: '24px' }}>
          <img
            src="/images/cloudcfo-logo-colored-icon-beside-text.png"
            className="img-responsive"
            style={{ height: '36px', justifySelf: 'center' }}
          />
          <h1 className="">Welcome Aboard!</h1>
          <p className="text-center pb0" style={{ maxWidth: '70%', justifySelf: 'center' }}>
            Welcome to your CloudCfo Account! Before we get started, let’s setup your new password.
          </p>
          <p className="usernameContainer">
            {tokenInvalid ? (
              <span style={{ color: 'red' }}>Token is invalid or has already expired.</span>
            ) : (
              <>Your username is: {user}</>
            )}
          </p>
        </div>
        <form
          className="reset-form usersForm"
          style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}
        >
          <div
            className="form-group"
            style={{ width: '100%', marginBottom: 24, textAlign: 'left' }}
          >
            <label>New Password</label>
            <div style={{ position: 'relative', width: '100%' }}>
              <input
                type={showPassword ? 'text' : 'password'}
                placeholder="Enter new password"
                value={newPassword}
                name="newPassword"
                onChange={handleChange}
                className="form-control bottom mb10"
                style={{ width: '100%', paddingRight: 40 }}
              />
              <span
                className="password-show"
                style={{
                  position: 'absolute',
                  right: 50,
                  bottom: '90%',
                  transform: 'translateY(-50%)',
                  cursor: 'pointer',
                  zIndex: 2,
                  height: 24,
                  display: 'flex',
                  alignItems: 'center',
                }}
                onClick={() => handleShowPassword('showPassword')}
              >
                <Icon
                  icon={showPassword ? ICONS.visible : ICONS.visible_off}
                  iconType={ICON_TYPES.filled}
                  style={{ fontSize: 24, color: 'black' }}
                />
              </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', marginTop: 4 }}>
              {newPassword.length > 0 && newPassword.length < 6 ? (
                <>
                  <i
                    className="material-icons-outlined mr5"
                    style={{ color: '#D34A46', fontSize: 20, marginRight: 4, marginTop: '-6px' }}
                  >
                    info
                  </i>
                  <span style={{ color: '#D34A46', fontSize: 14 }}>
                    Password should be at least 6 characters
                  </span>
                </>
              ) : (
                <span style={{ color: '#999', fontSize: 14 }}>
                  Password should be at least 6 characters
                </span>
              )}
            </div>
          </div>
          <div
            className="form-group"
            style={{ width: '100%', marginBottom: 24, textAlign: 'left' }}
          >
            <label>Confirm New Password</label>
            <div style={{ position: 'relative', width: '100%' }}>
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                placeholder="Confirm password"
                value={confirmPassword}
                name="confirmPassword"
                onChange={handleChange}
                className="form-control bottom"
                style={{ width: '100%', paddingRight: 40 }}
              />
              <span
                className="password-show"
                style={{
                  position: 'absolute',
                  right: 50,
                  bottom: '90%',
                  transform: 'translateY(-50%)',
                  cursor: 'pointer',
                  zIndex: 2,
                  height: 24,
                  display: 'flex',
                  alignItems: 'center',
                }}
                onClick={() => handleShowPassword('showConfirmPassword')}
              >
                <Icon
                  icon={showConfirmPassword ? ICONS.visible : ICONS.visible_off}
                  iconType={ICON_TYPES.filled}
                  style={{ fontSize: 24, color: 'black' }}
                />
              </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', marginTop: 4 }}>
              {!isValid && confirmPassword !== '' ? (
                <>
                  <i
                    className="material-icons-outlined mr5"
                    style={{ color: '#D34A46', fontSize: 20, marginRight: 4, marginTop: '-6px' }}
                  >
                    info
                  </i>
                  <span style={{ color: '#D34A46', fontSize: 14 }}>Passwords do not match!</span>
                </>
              ) : (
                isValid && (
                  <span
                    style={{
                      color: 'black',
                      fontSize: 14,
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    <i
                      className="material-icons"
                      style={{ fontSize: 20, marginRight: 4, marginTop: -6 }}
                    >
                      check_circle_outline
                    </i>
                  </span>
                )
              )}
            </div>
          </div>

          {state.passwordCreated && showLinkInvalid && (
            <div
              className="tw-text-cfo-red-400"
              style={{ marginTop: 12, fontWeight: 400, textAlign: 'center' }}
            >
              <span>
                This link is no longer valid since a password has already been successfully created.
              </span>
            </div>
          )}

          <div style={{ width: '100%', display: 'flex', justifyContent: 'center', marginTop: 40 }}>
            {isValid &&
            newPassword.length >= 6 &&
            confirmPassword.length >= 6 &&
            !state.tokenInvalid &&
            !state.passwordCreated ? (
              <a
                className={`btn mt40${state.submitting ? ' disabled' : ''}`}
                style={{
                  margin: 'auto',
                  textAlign: 'center',
                  cursor: state.submitting ? 'not-allowed' : 'pointer',
                }}
                onClick={state.submitting ? undefined : handleSubmit}
              >
                Set Password
              </a>
            ) : (
              <a
                className="btn disabled mt40"
                style={{ margin: 'auto', textAlign: 'center', cursor: 'not-allowed', opacity: 0.6 }}
              >
                Set Password
              </a>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default NewPassword;
